import { ApiSalesDashboardService } from 'services';
import endpoints from 'constants/endpoints';
import {
	AllPaymentsFilters,
	AllPaymentsResponse,
	CorporateCard,
	CorporateCardPayment,
	RegisterPaymentBody,
	SaleOrderPaymentsResponse,
} from './types';

const ep = endpoints.corporateCards;

export const fetchPaymentsBySaleOrder = async (saleOrderId: string): Promise<SaleOrderPaymentsResponse> => {
	const url = ep.paymentsBySaleOrder.replace(':saleOrderId', saleOrderId);
	const res = await ApiSalesDashboardService.get<{ success: boolean; data: SaleOrderPaymentsResponse }>(url);
	return res.data.data;
};

export const fetchPaymentsByService = async (serviceId: string): Promise<SaleOrderPaymentsResponse> => {
	const url = ep.paymentsByService.replace(':serviceId', serviceId);
	const res = await ApiSalesDashboardService.get<{ success: boolean; data: SaleOrderPaymentsResponse }>(url);
	return res.data.data;
};

export const fetchAvailableCards = async (): Promise<CorporateCard[]> => {
	const res = await ApiSalesDashboardService.get<{ success: boolean; data: { cards: CorporateCard[] } }>(
		ep.availableCards,
	);
	return res.data.data.cards;
};

export const registerPayment = async (body: RegisterPaymentBody): Promise<CorporateCardPayment> => {
	const res = await ApiSalesDashboardService.post<{ success: boolean; data: CorporateCardPayment }>(ep.payments, body);
	return res.data.data;
};

export const annulPayment = async (id: number, reason: string): Promise<CorporateCardPayment> => {
	const url = ep.annulPayment.replace(':id', String(id));
	const res = await ApiSalesDashboardService.post<{ success: boolean; data: CorporateCardPayment }>(url, { reason });
	return res.data.data;
};

export const adjustPaymentByService = async (serviceId: string, note?: string): Promise<CorporateCardPayment> => {
	const url = ep.adjustByService.replace(':serviceId', serviceId);
	const res = await ApiSalesDashboardService.post<{ success: boolean; data: CorporateCardPayment }>(
		url,
		note ? { note } : {},
	);
	return res.data.data;
};

const buildPaymentParams = (filters: Partial<AllPaymentsFilters>): Record<string, string | number> => {
	const params: Record<string, string | number> = {};
	Object.entries(filters).forEach(([key, val]) => {
		if (val !== '' && val !== undefined && val !== null) {
			params[key] = val;
		}
	});
	return params;
};

export const fetchAllPayments = async (filters: Partial<AllPaymentsFilters>): Promise<AllPaymentsResponse> => {
	const params = buildPaymentParams(filters);
	const res = await ApiSalesDashboardService.get<{ success: boolean; data: AllPaymentsResponse }>(ep.payments, {
		params,
	});
	return res.data.data;
};

export const exportPaymentsCsv = async (filters: Partial<AllPaymentsFilters>): Promise<void> => {
	// Excluir limit y offset — la exportación trae todo el set filtrado
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { limit, offset, ...rest } = filters as AllPaymentsFilters;
	const params = buildPaymentParams(rest);

	const res = await ApiSalesDashboardService.get(ep.paymentsExport, {
		params,
		responseType: 'blob',
	});

	// Parsear Content-Disposition para obtener el nombre del archivo
	const disposition: string = res.headers['content-disposition'] ?? '';
	const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'";\n]+)\1/);
	const filename = match?.[2]?.trim() ?? `pagos-tarjetas-${new Date().toISOString().slice(0, 10)}.csv`;

	const url = URL.createObjectURL(new Blob([res.data as BlobPart], { type: 'text/csv;charset=utf-8;' }));
	const link = document.createElement('a');
	link.href = url;
	link.setAttribute('download', filename);
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};

export const fetchAllCards = async (): Promise<CorporateCard[]> => {
	const res = await ApiSalesDashboardService.get<{ success: boolean; data: { cards: CorporateCard[] } }>(ep.cards);
	return res.data.data.cards;
};

export const createCard = async (body: {
	brand: string;
	bank: string;
	last4: string;
	holder: string;
	notes?: string;
	closingDay?: number;
	dueDay?: number;
}): Promise<CorporateCard> => {
	const res = await ApiSalesDashboardService.post<{ success: boolean; data: CorporateCard }>(ep.cards, body);
	return res.data.data;
};

export const updateCard = async (
	id: number,
	body: Partial<{
		brand: string;
		bank: string;
		last4: string;
		holder: string;
		notes: string;
		enabled: boolean;
		closingDay: number | null;
		dueDay: number | null;
	}>,
): Promise<CorporateCard> => {
	const url = ep.cardById.replace(':id', String(id));
	const res = await ApiSalesDashboardService.patch<{ success: boolean; data: CorporateCard }>(url, body);
	return res.data.data;
};

export interface AssignmentResult {
	assigned: string[];
	alreadyAssigned: string[];
}

export const assignUsersToCard = async (
	cardId: number,
	emails: string[],
): Promise<{ card: CorporateCard; assignmentResult: AssignmentResult }> => {
	const url = ep.cardAssignments.replace(':id', String(cardId));
	const res = await ApiSalesDashboardService.post<{
		success: boolean;
		data: CorporateCard & { assignmentResult: AssignmentResult };
	}>(url, { emails });
	const { assignmentResult, ...card } = res.data.data;
	return { card: card as CorporateCard, assignmentResult };
};

export const removeUserFromCard = async (cardId: number, userId: number): Promise<void> => {
	const url = ep.cardAssignmentDelete.replace(':cardId', String(cardId)).replace(':userId', String(userId));
	await ApiSalesDashboardService.delete(url);
};
